import {
  getColor,
  getFontFamily,
  getBorder,
  getBrandStyleBorder,
  getContentBlockPadding,
  getButtonWidth,
  getLineHeight,
} from "containers/admin/clients/client/theme-page/preview/helpers/bee-data-formatter/bee-data-formatter-helper";

import { BeeConfigExtended, BrandStyleData } from "types/bee";

import {
  BeeFontData,
  ColorsFormData,
  APIFormatThemeFontsData,
  APIFormatThemeTypographyData,
  APIFormatThemeButtonData,
  APIFormatThemeLayoutData,
} from "containers/admin/clients/client/theme-page/preview/preview.types";
import {
  MOBILE_VALUE,
  DESKTOP_VALUE,
  LEFT_VALUE,
  RIGHT_VALUE,
  TOP_VALUE,
  BOTTOM_VALUE,
  HEADINGS_VALUE,
  PARAGRAPH_VALUE,
  BUTTON_VALUE,
} from "containers/admin/clients/client/theme-page/preview/sub-tabs/layout/layout.constants";

export const formatDataToBeeConfigs = ({
  colors,
  fonts,
  typography,
  button,
  banner,
  layout,
}: {
  colors: ColorsFormData;
  fonts: APIFormatThemeFontsData;
  typography: APIFormatThemeTypographyData;
  button: APIFormatThemeButtonData;
  banner: { imageUrl: string };
  layout: APIFormatThemeLayoutData;
}) => {
  if (!colors && !fonts && !typography && !button && !banner && !layout) return null;

  const updatedBeeFonts = fonts?.beeFonts?.map((font: BeeFontData) => {
    const { value: _value, ...rest } = font;

    return rest;
  });

  const beeConfig: BeeConfigExtended = {
    customAttributes: {
      attributes: [
        {
          key: "image-type",
          value: ["banner"],
          target: "tag",
        },
      ],
    },
    editorFonts: {
      showDefaultFonts: false,
      customFonts: updatedBeeFonts || [],
    },
    titleDefaultConfig: {
      bold: false,
    },
    titleDefaultStyles: {
      h1: {
        color: getColor(typography.heading.textColor, colors),
        "font-size": typography.heading.fontSize,
        "font-family": getFontFamily(
          typography.heading.fontFamily,
          fonts.beeFonts,
          typography.heading.fallbackFontFamily,
        ),
        "font-weight": typography.heading.fontWeight,
        "text-align": typography.heading.alignment || "left",
        "line-height": getLineHeight(typography.heading.lineHeight),
        "link-color": getColor(typography?.paragraph?.linkColor || "", colors),
      },
      h2: {
        color: getColor(typography.subHeading.textColor, colors),
        "font-size": typography.subHeading.fontSize,
        "font-family": getFontFamily(
          typography.subHeading.fontFamily,
          fonts.beeFonts,
          typography.subHeading.fallbackFontFamily,
        ),
        "font-weight": typography.subHeading.fontWeight,
        "text-align": typography.subHeading.alignment || "left",
        "line-height": getLineHeight(typography.subHeading.lineHeight),
        "link-color": getColor(typography?.paragraph?.linkColor || "", colors),
      },
      h3: {
        color: getColor(typography.paragraph.textColor, colors),
        "font-size": typography.paragraph.fontSize,
        "font-family": getFontFamily(
          typography.paragraph.fontFamily,
          fonts.beeFonts,
          typography.paragraph.fallbackFontFamily,
        ),
        "font-weight": typography.paragraph.fontWeight,
        "text-align": typography.paragraph.alignment || "left",
        "line-height": getLineHeight(typography.paragraph.lineHeight),
        "link-color": getColor(typography?.paragraph?.linkColor || "", colors),
      },
    },
    contentDefaults: {
      button: {
        width: getButtonWidth(button.width),
        styles: {
          color: getColor(typography.buttonLabel.textColor, colors),
          fontSize: typography.buttonLabel.fontSize,
          fontFamily: getFontFamily(
            typography.buttonLabel.fontFamily,
            fonts.beeFonts,
            typography.buttonLabel.fallbackFontFamily,
          ),
          fontWeight: typography.buttonLabel.fontWeight,
          backgroundColor: getColor(button.backgroundColor, colors),
          borderBottom: getBorder(button.border, colors),
          borderLeft: getBorder(button.border, colors),
          borderRight: getBorder(button.border, colors),
          borderTop: getBorder(button.border, colors),
          borderRadius: button.border.radius,
          lineHeight: getLineHeight(typography.buttonLabel.lineHeight),
          paddingLeft: `${button.padding.left}px`,
          paddingRight: `${button.padding.right}px`,
          paddingBottom: `${button.padding.bottom}px`,
          paddingTop: `${button.padding.top}px`,
        },
        blockOptions: {
          align: button.alignment || "left",
          paddingBottom: getContentBlockPadding(layout, BUTTON_VALUE, DESKTOP_VALUE, BOTTOM_VALUE),
          paddingLeft: getContentBlockPadding(layout, BUTTON_VALUE, DESKTOP_VALUE, LEFT_VALUE),
          paddingRight: getContentBlockPadding(layout, BUTTON_VALUE, DESKTOP_VALUE, RIGHT_VALUE),
          paddingTop: getContentBlockPadding(layout, BUTTON_VALUE, DESKTOP_VALUE, TOP_VALUE),
        },
        mobileStyles: {
          paddingBottom: getContentBlockPadding(layout, BUTTON_VALUE, MOBILE_VALUE, BOTTOM_VALUE),
          paddingLeft: getContentBlockPadding(layout, BUTTON_VALUE, MOBILE_VALUE, LEFT_VALUE),
          paddingRight: getContentBlockPadding(layout, BUTTON_VALUE, MOBILE_VALUE, RIGHT_VALUE),
          paddingTop: getContentBlockPadding(layout, BUTTON_VALUE, MOBILE_VALUE, TOP_VALUE),
        },
      },
      list: {
        styles: {
          color: getColor(typography.paragraph.textColor, colors),
          fontSize: typography.paragraph.fontSize,
          fontFamily: getFontFamily(
            typography.paragraph.fontFamily,
            fonts.beeFonts,
            typography.paragraph.fallbackFontFamily,
          ),
          fontWeight: typography.paragraph.fontWeight,
          lineHeight: getLineHeight(typography.paragraph.lineHeight),
          linkColor: getColor(typography?.paragraph?.linkColor || "", colors),
        },
      },
      paragraph: {
        styles: {
          color: getColor(typography.paragraph.textColor, colors),
          fontSize: typography.paragraph.fontSize,
          fontFamily: getFontFamily(
            typography.paragraph.fontFamily,
            fonts.beeFonts,
            typography.paragraph.fallbackFontFamily,
          ),
          textAlign: typography.paragraph.alignment || "left",
          fontWeight: typography.paragraph.fontWeight,
          lineHeight: getLineHeight(typography.paragraph.lineHeight),
          linkColor: getColor(typography?.paragraph?.linkColor || "", colors),
        },
        blockOptions: {
          paddingBottom: getContentBlockPadding(
            layout,
            PARAGRAPH_VALUE,
            DESKTOP_VALUE,
            BOTTOM_VALUE,
          ),
          paddingLeft: getContentBlockPadding(layout, PARAGRAPH_VALUE, DESKTOP_VALUE, LEFT_VALUE),
          paddingRight: getContentBlockPadding(layout, PARAGRAPH_VALUE, DESKTOP_VALUE, RIGHT_VALUE),
          paddingTop: getContentBlockPadding(layout, PARAGRAPH_VALUE, DESKTOP_VALUE, TOP_VALUE),
        },
        mobileStyles: {
          paddingBottom: getContentBlockPadding(
            layout,
            PARAGRAPH_VALUE,
            MOBILE_VALUE,
            BOTTOM_VALUE,
          ),
          paddingLeft: getContentBlockPadding(layout, PARAGRAPH_VALUE, MOBILE_VALUE, LEFT_VALUE),
          paddingRight: getContentBlockPadding(layout, PARAGRAPH_VALUE, MOBILE_VALUE, RIGHT_VALUE),
          paddingTop: getContentBlockPadding(layout, PARAGRAPH_VALUE, MOBILE_VALUE, TOP_VALUE),
        },
      },
      row: {
        styles: {
          paddingBottom: `${layout.contentArea.bottom}px`,
          paddingLeft: `${layout.contentArea.left}px`,
          paddingRight: `${layout.contentArea.right}px`,
          paddingTop: `${layout.contentArea.top}px`,
        },
      },
      title: {
        defaultHeadingLevel: "h1",
        blockOptions: {
          paddingBottom: getContentBlockPadding(
            layout,
            HEADINGS_VALUE,
            DESKTOP_VALUE,
            BOTTOM_VALUE,
          ),
          paddingLeft: getContentBlockPadding(layout, HEADINGS_VALUE, DESKTOP_VALUE, LEFT_VALUE),
          paddingRight: getContentBlockPadding(layout, HEADINGS_VALUE, DESKTOP_VALUE, RIGHT_VALUE),
          paddingTop: getContentBlockPadding(layout, HEADINGS_VALUE, DESKTOP_VALUE, TOP_VALUE),
        },
        mobileStyles: {
          paddingBottom: getContentBlockPadding(layout, HEADINGS_VALUE, MOBILE_VALUE, BOTTOM_VALUE),
          paddingLeft: getContentBlockPadding(layout, HEADINGS_VALUE, MOBILE_VALUE, LEFT_VALUE),
          paddingRight: getContentBlockPadding(layout, HEADINGS_VALUE, MOBILE_VALUE, RIGHT_VALUE),
          paddingTop: getContentBlockPadding(layout, HEADINGS_VALUE, MOBILE_VALUE, TOP_VALUE),
        },
      },
    },
    banner: banner.imageUrl,
  };

  const brandStyleConfig: BrandStyleData = {
    row: {
      contentStyles: {
        paddingBottom: `${layout.contentArea.bottom}px`,
        paddingLeft: `${layout.contentArea.left}px`,
        paddingRight: `${layout.contentArea.right}px`,
        paddingTop: `${layout.contentArea.top}px`,
      },
    },
    h1: {
      fontFamily: getFontFamily(
        typography.heading.fontFamily,
        fonts.beeFonts,
        typography.heading.fallbackFontFamily,
      ),
      fontSize: typography.heading.fontSize,
      textAlign: typography.heading.alignment || "left",
      lineHeight: getLineHeight(typography.heading.lineHeight),
      fontWeight: typography.heading.fontWeight,
      color: getColor(typography.heading.textColor, colors),
      blockOptions: {
        paddingBottom: getContentBlockPadding(layout, HEADINGS_VALUE, DESKTOP_VALUE, BOTTOM_VALUE),
        paddingLeft: getContentBlockPadding(layout, HEADINGS_VALUE, DESKTOP_VALUE, LEFT_VALUE),
        paddingRight: getContentBlockPadding(layout, HEADINGS_VALUE, DESKTOP_VALUE, RIGHT_VALUE),
        paddingTop: getContentBlockPadding(layout, HEADINGS_VALUE, DESKTOP_VALUE, TOP_VALUE),
      },
    },
    h2: {
      fontFamily: getFontFamily(
        typography.subHeading.fontFamily,
        fonts.beeFonts,
        typography.subHeading.fallbackFontFamily,
      ),
      fontSize: typography.subHeading.fontSize,
      textAlign: typography.subHeading.alignment || "left",
      lineHeight: getLineHeight(typography.subHeading.lineHeight),
      fontWeight: typography.subHeading.fontWeight,
      color: getColor(typography.subHeading.textColor, colors),
      blockOptions: {
        paddingBottom: getContentBlockPadding(layout, HEADINGS_VALUE, DESKTOP_VALUE, BOTTOM_VALUE),
        paddingLeft: getContentBlockPadding(layout, HEADINGS_VALUE, DESKTOP_VALUE, LEFT_VALUE),
        paddingRight: getContentBlockPadding(layout, HEADINGS_VALUE, DESKTOP_VALUE, RIGHT_VALUE),
        paddingTop: getContentBlockPadding(layout, HEADINGS_VALUE, DESKTOP_VALUE, TOP_VALUE),
      },
    },
    button: {
      styles: {
        fontFamily: getFontFamily(
          typography.buttonLabel.fontFamily,
          fonts.beeFonts,
          typography.buttonLabel.fallbackFontFamily,
        ),
        fontSize: typography.buttonLabel.fontSize,
        lineHeight: getLineHeight(typography.buttonLabel.lineHeight),
        fontWeight: typography.buttonLabel.fontWeight,
        color: getColor(typography.buttonLabel.textColor, colors),
        paddingLeft: `${button.padding.left}px`,
        paddingRight: `${button.padding.right}px`,
        paddingBottom: `${button.padding.bottom}px`,
        paddingTop: `${button.padding.top}px`,
        backgroundColor: getColor(button.backgroundColor, colors),
        borderRadius: button.border.radius,
        borderBottom: getBrandStyleBorder(button.border, colors),
        borderLeft: getBrandStyleBorder(button.border, colors),
        borderRight: getBrandStyleBorder(button.border, colors),
        borderTop: getBrandStyleBorder(button.border, colors),
      },
      width: getButtonWidth(button.width),
      blockOptions: {
        align: button.alignment || "left",
        paddingBottom: getContentBlockPadding(layout, BUTTON_VALUE, DESKTOP_VALUE, BOTTOM_VALUE),
        paddingLeft: getContentBlockPadding(layout, BUTTON_VALUE, DESKTOP_VALUE, LEFT_VALUE),
        paddingRight: getContentBlockPadding(layout, BUTTON_VALUE, DESKTOP_VALUE, RIGHT_VALUE),
        paddingTop: getContentBlockPadding(layout, BUTTON_VALUE, DESKTOP_VALUE, TOP_VALUE),
      },
      mobileStyles: {
        paddingBottom: getContentBlockPadding(layout, BUTTON_VALUE, MOBILE_VALUE, BOTTOM_VALUE),
        paddingLeft: getContentBlockPadding(layout, BUTTON_VALUE, MOBILE_VALUE, LEFT_VALUE),
        paddingRight: getContentBlockPadding(layout, BUTTON_VALUE, MOBILE_VALUE, RIGHT_VALUE),
        paddingTop: getContentBlockPadding(layout, BUTTON_VALUE, MOBILE_VALUE, TOP_VALUE),
      },
    },
    paragraph: {
      styles: {
        fontSize: typography.paragraph.fontSize,
        fontFamily: getFontFamily(
          typography.paragraph.fontFamily,
          fonts.beeFonts,
          typography.paragraph.fallbackFontFamily,
        ),
        textAlign: typography.paragraph.alignment || "left",
        lineHeight: getLineHeight(typography.paragraph.lineHeight),
        fontWeight: typography.paragraph.fontWeight,
        color: getColor(typography.paragraph.textColor, colors),
        linkColor: getColor(typography?.paragraph?.linkColor || "", colors),
      },
      blockOptions: {
        paddingBottom: getContentBlockPadding(layout, PARAGRAPH_VALUE, DESKTOP_VALUE, BOTTOM_VALUE),
        paddingLeft: getContentBlockPadding(layout, PARAGRAPH_VALUE, DESKTOP_VALUE, LEFT_VALUE),
        paddingRight: getContentBlockPadding(layout, PARAGRAPH_VALUE, DESKTOP_VALUE, RIGHT_VALUE),
        paddingTop: getContentBlockPadding(layout, PARAGRAPH_VALUE, DESKTOP_VALUE, TOP_VALUE),
      },
      mobileStyles: {
        paddingBottom: getContentBlockPadding(layout, PARAGRAPH_VALUE, MOBILE_VALUE, BOTTOM_VALUE),
        paddingLeft: getContentBlockPadding(layout, PARAGRAPH_VALUE, MOBILE_VALUE, LEFT_VALUE),
        paddingRight: getContentBlockPadding(layout, PARAGRAPH_VALUE, MOBILE_VALUE, RIGHT_VALUE),
        paddingTop: getContentBlockPadding(layout, PARAGRAPH_VALUE, MOBILE_VALUE, TOP_VALUE),
      },
    },
    list: {
      styles: {
        fontSize: typography.paragraph.fontSize,
        fontFamily: getFontFamily(
          typography.paragraph.fontFamily,
          fonts.beeFonts,
          typography.paragraph.fallbackFontFamily,
        ),
        textAlign: typography.paragraph.alignment || "left",
        lineHeight: getLineHeight(typography.paragraph.lineHeight),
        fontWeight: typography.paragraph.fontWeight,
        color: getColor(typography.paragraph.textColor, colors),
        linkColor: getColor(typography?.paragraph?.linkColor || "", colors),
      },
      blockOptions: {
        paddingBottom: getContentBlockPadding(layout, PARAGRAPH_VALUE, DESKTOP_VALUE, BOTTOM_VALUE),
        paddingLeft: getContentBlockPadding(layout, PARAGRAPH_VALUE, DESKTOP_VALUE, LEFT_VALUE),
        paddingRight: getContentBlockPadding(layout, PARAGRAPH_VALUE, DESKTOP_VALUE, RIGHT_VALUE),
        paddingTop: getContentBlockPadding(layout, PARAGRAPH_VALUE, DESKTOP_VALUE, TOP_VALUE),
      },
      mobileStyles: {
        paddingBottom: getContentBlockPadding(layout, PARAGRAPH_VALUE, MOBILE_VALUE, BOTTOM_VALUE),
        paddingLeft: getContentBlockPadding(layout, PARAGRAPH_VALUE, MOBILE_VALUE, LEFT_VALUE),
        paddingRight: getContentBlockPadding(layout, PARAGRAPH_VALUE, MOBILE_VALUE, RIGHT_VALUE),
        paddingTop: getContentBlockPadding(layout, PARAGRAPH_VALUE, MOBILE_VALUE, TOP_VALUE),
      },
    },
  };

  // Only include image if banner.imageUrl exists
  if (banner?.imageUrl) {
    brandStyleConfig.image = {
      className: "banner",
      src: banner.imageUrl,
    };
  }

  return {
    beeConfig,
    brandStyleConfig,
  };
};
